import { useCallback, useMemo, useRef, useState } from 'react';
import type React from 'react';

import { FocusRing, Image } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import type { ShareAsset } from '../../../../../../../shared/types';
import { usePreviewUrls } from '../usePreviewUrls';

/**
 * Keyed by `variationName`, never by position: asset order is not guaranteed stable across petitions, so
 * a position-keyed palette would show one colour and share another.
 *
 * `story_variant_b` is the exception — its background is a blur of the petition's own photo, so no constant
 * matches every petition. It carries a fixed colour anyway: an approximate dot reads better at 24px than a
 * neutral grey, which looks like a swatch that failed to load.
 */
const SWATCH_COLOR_BY_VARIATION: Readonly<Record<string, string>> = {
	story_variant_a: '#F34F4B',
	story_variant_b: '#9AA07B',
	story_variant_c: '#F66864',
	story_variant_d: '#413B49',
	// The photo-less templates, offered when a petition has no image of its own.
	story_variant_e: '#F66864',
	story_variant_f: '#F34F4B',
};

const FALLBACK_SWATCH_COLOR = '#6B7280';

/**
 * Height first, width derived from the 9:16 asset ratio — the same sizing variant 1 uses, so the two
 * arms differ in how an asset is chosen rather than in how large it looks.
 */
const CARD_SIZE_VARS = {
	'--share-card-h': '42svh',
	'--share-card-w': 'calc(42svh * 9 / 16)',
} as React.CSSProperties;

/**
 * Variant 2 of the multiple-vertical-share-assets experiment (CHANGE-95456): one asset at full size with
 * a row of colour swatches beneath it.
 *
 * The tap *is* the selection, which is why none of the carousel's scroll-settling machinery appears here.
 */
export const ShareVisualSwatches: React.FC<Props> = ({
	assets,
	label,
	itemLabel,
	onActiveIndexChange,
	onInteraction,
	onSelectionChange,
}) => {
	// Memoized: usePreviewUrls re-mints its object URLs whenever this array's identity changes, revoking
	// the ones the rendered `<img>` still points at.
	const previews = useMemo(() => assets.map(({ file }) => file), [assets]);
	const previewUrls = usePreviewUrls(previews);
	const [selectedIndex, setSelectedIndex] = useState(0);
	// Seeded to -1, matching the carousel: 0 is a real asset, so it must not start out looking reported.
	const reportedIndexRef = useRef(-1);

	const handleSelect = useCallback(
		(index: number) => {
			// Ahead of the dedup below: tapping the already-selected swatch changes nothing, but the
			// supporter did engage with the picker, which is what `share_asset_interacted` records.
			onInteraction();

			if (index !== selectedIndex) {
				setSelectedIndex(index);
				onActiveIndexChange(index);
			}

			// Deduped against what was last reported rather than against what is displayed, and seeded to -1
			// so index 0 does not start out looking reported. Comparing to `selectedIndex` swallowed the
			// first tap on the default asset — the "this one, I'm sure" gesture — which variant 1 does
			// report. The two arms are measured against each other, so the semantics have to match.
			// eslint-disable-next-line react-hooks/refs -- event handlers only.
			if (reportedIndexRef.current === index) return;
			// eslint-disable-next-line react-hooks/refs -- same.
			reportedIndexRef.current = index;
			onSelectionChange(index, 'tap');
		},
		[selectedIndex, onInteraction, onActiveIndexChange, onSelectionChange],
	);

	return (
		<div className="flex flex-col gap-4" style={CARD_SIZE_VARS}>
			{/*
			 * Height reserved on the shell rather than bailing out until the object URLs exist, matching the
			 * carousel's scroll container. usePreviewUrls mints them in an effect, so the first render has
			 * none, and returning null there would let the modal lay out without the picker and then reflow
			 * once it arrives.
			 */}
			<div className="flex h-[var(--share-card-h)] justify-center">
				{previewUrls.length > 0 && (
					<Image
						className="h-[var(--share-card-h)] w-[var(--share-card-w)] rounded-xl object-cover"
						aspectRatio="9:16"
						src={previewUrls[selectedIndex]}
						alt={itemLabel(selectedIndex)}
					/>
				)}
			</div>

			{/* `role="group"` so the label is announced: assistive tech ignores aria-label on a plain div. */}
			<div
				// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- not fixed to avoid regression risk from an automated markup rewrite; needs manual accessibility review
				role="group"
				aria-label={label}
				data-qa="share-visual-swatches"
				className="flex min-h-6 justify-center gap-6"
			>
				{previewUrls.map((_, index) => (
					// The list is fixed-length and never reordered within a mount, and the object URL is not a
					// usable key — the test environment's createObjectURL stub returns one value for every call.
					// eslint-disable-next-line react/no-array-index-key
					<FocusRing key={`share-visual-swatch-${index}`} focusRingClass="rounded-full">
						<button
							type="button"
							aria-current={selectedIndex === index}
							aria-label={itemLabel(index)}
							data-qa="share-visual-swatch"
							data-testid="share-visual-swatch"
							onClick={() => handleSelect(index)}
							// The outline carries the selected state alongside the colour: WCAG 1.4.1 does not allow
							// colour to carry it alone, and colour is the only other differentiator here.
							//
							// `before` widens the hit area to 44px (WCAG 2.5.5) without affecting layout, so the
							// visible dot stays 24px and the gap between dots stays a real 24px.
							className={cn(
								'relative size-6 shrink-0 rounded-full before:absolute before:-inset-2.5 before:content-[""]',
								selectedIndex === index && 'outline-stroke-selected outline outline-2 outline-offset-2',
							)}
							style={{
								backgroundColor: SWATCH_COLOR_BY_VARIATION[assets[index]?.variationName ?? ''] ?? FALLBACK_SWATCH_COLOR,
							}}
						/>
					</FocusRing>
				))}
			</div>
		</div>
	);
};

type Props = {
	/** File and variation together, so the swatch colour cannot drift from the asset that gets shared. */
	assets: readonly ShareAsset[];
	label: string;
	/** Accessible name for one swatch. Never the colour — "red" tells a screen-reader user nothing. */
	itemLabel: (index: number) => string;
	/** Fired on every change, immediately — this decides which asset Continue ships. */
	onActiveIndexChange: (selectedIndex: number) => void;
	/** Fired on every tap, including one on the already-selected asset, which changes nothing. */
	onInteraction: () => void;
	onSelectionChange: (selectedIndex: number, interactionType: 'swipe' | 'tap') => void;
};
